import AddRoundedIcon from "@mui/icons-material/AddRounded";
import { Alert, Button, Stack, Snackbar } from "@mui/material";
import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { createOssChange, fetchOssChanges, fetchOssOrders, reviewOssChange } from "../../api/ossApi";
import { OssChangeList } from "../../components/changes/OssChangeList";
import { OssChangeRequestDialog } from "../../components/changes/OssChangeRequestDialog";
import { OssMetricCards } from "../../components/common/OssMetricCards";
import { OssPanel } from "../../components/common/OssPanel";
import { OssPageHeader } from "../../components/common/OssPageHeader";
import { useOssAsyncData } from "../../hooks/useOssAsyncData";

export const OssChangeReview = () => {
  const { group_code, project_code } = useParams();
  const [open, setOpen] = useState(false);
  const [toast, setToast] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { data, loading, error, setError, load } = useOssAsyncData({
    fetcher: () => fetchOssChanges(group_code, project_code, { pageSize: 50 }),
    fallbackData: { items: [], pagination: { total: 0 } },
    errorMessage: "변경 검토 목록을 불러오지 못했습니다.",
    deps: [group_code, project_code],
  });
  const { data: orderData } = useOssAsyncData({
    fetcher: () => fetchOssOrders(group_code, project_code, { pageSize: 100 }),
    fallbackData: { items: [] },
    errorMessage: "주문 목록을 불러오지 못했습니다.",
    deps: [group_code, project_code],
  });
  const metrics = useMemo(() => {
    const rows = data.items || [];
    const reviewCount = rows.filter((row) => ["REQUESTED", "REVIEWING"].includes(row.request_status)).length;
    const approvedCount = rows.filter((row) => ["APPROVED", "APPLIED"].includes(row.request_status)).length;
    const rejectedCount = rows.filter((row) => row.request_status === "REJECTED").length;
    return [
      { label: "전체 요청", value: Number(data.pagination?.total || rows.length).toLocaleString(), unit: "건", tone: "#6c5ce7", iconName: "checklistIcon" },
      { label: "검토 중", value: reviewCount.toLocaleString(), unit: "건", tone: "#f59e0b", iconName: "checklistIcon" },
      { label: "승인/적용", value: approvedCount.toLocaleString(), unit: "건", tone: "#10b981", iconName: "checklistIcon" },
      { label: "반려", value: rejectedCount.toLocaleString(), unit: "건", tone: "#ef4444", iconName: "warningIcon" },
    ];
  }, [data.items, data.pagination?.total]);

  const submitChange = async (payload) => {
    setSubmitting(true);
    setError("");
    try {
      await createOssChange(group_code, project_code, payload);
      setOpen(false);
      setToast("변경 요청을 등록했습니다.");
      await load();
    } catch (e) {
      setError(e.response?.data?.message || "변경 요청 등록 중 오류가 발생했습니다.");
    } finally {
      setSubmitting(false);
    }
  };

  const review = async (row, status) => {
    setSubmitting(true);
    setError("");
    try {
      await reviewOssChange(group_code, project_code, row.change_request_id, {
        requestStatus: status,
        reviewedByUserKey: "order.manager",
      });
      setToast(status === "APPROVED" ? "변경 요청을 승인했습니다." : "변경 요청을 반려했습니다.");
      await load();
    } catch (e) {
      setError(e.response?.data?.message || "변경 검토 처리 중 오류가 발생했습니다.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Stack spacing={2}>
      <OssPageHeader
        title="변경 검토"
        iconName="checklistIcon"
        actions={<Button variant="contained" startIcon={<AddRoundedIcon />} onClick={() => setOpen(true)}>변경 요청</Button>}
      />
      {error && <Alert severity="warning">{error}</Alert>}
      <OssMetricCards items={metrics} />
      <OssPanel sx={{ p: 1.25, overflow: "hidden", bgcolor: "#fff" }}>
        <OssChangeList rows={data.items} loading={loading || submitting} onReview={review} />
      </OssPanel>
      <OssChangeRequestDialog
        open={open}
        orders={orderData.items}
        loading={submitting}
        onClose={() => setOpen(false)}
        onSubmit={submitChange}
      />
      <Snackbar open={Boolean(toast)} autoHideDuration={2500} onClose={() => setToast("")} message={toast} />
    </Stack>
  );
};
